import { useState, useMemo, useCallback, useEffect } from 'react';
import { onSnapshot, collection } from 'firebase/firestore';
import { db } from '../../../../firebase/config';
import { deleteClienteCrosellingFull } from '../../../../firebase/firebaseUtils';

// CONFIGURACIÓN INICIAL DE VISIBILIDAD DE COLUMNAS (TODAS HABILITADAS POR DEFECTO)
const DEFAULT_COLUMN_VISIBILITY = {
    fechaCarga: true,
    displayNombre: true,
    displayDoc: true,
    productosDisplay: true,
    estadoGestion: true,
    responsableAsignado: true,
    fechaProximoContacto: true,
    canal: true,
    canalNombre: true,
    subcanalNombre: true
};

export const useClientesCrosellingTable = () => {
    // ESTADOS PRINCIPALES DEL MÓDULO
    const [view, setView] = useState('list'); // 'list' | 'detail'
    const [rawItems, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingRecord, setEditingRecord] = useState(null);
    const [detailRecord, setDetailRecord] = useState(null);

    // CONFIGURACIÓN DE MODAL DE CONFIRMACIÓN / ELIMINACIÓN
    const [authModalConfig, setAuthModalConfig] = useState({
        open: false,
        type: '',
        item: null
    });

    // 🎯 ORDENAMIENTO POR DEFECTO: AGENDA CONTACTO (MÁS PRÓXIMAS PRIMERO)
    const [sortConfig, setSortConfig] = useState({ key: 'fechaProximoContacto', direction: 'asc' });
    const [columnVisibility, setColumnVisibility] = useState(DEFAULT_COLUMN_VISIBILITY);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(15);
    const [selectedRowId, setSelectedRowId] = useState(null);
    const [filterValues, setFilterValues] = useState({});

    // 1. ESCUCHA EN TIEMPO REAL A FIRESTORE
    useEffect(() => {
        setLoading(true);
        const refCollection = collection(db, 'public/data/clientes_croselling');

        const unsubscribe = onSnapshot(refCollection, (snapshot) => {
            const list = snapshot.docs.map(doc => {
                const data = doc.data();

                // Normalización de Nombre de Cliente / Prospecto
                const nombreComp = [data.clienteNombre, data.clienteApellido].filter(Boolean).join(' ').trim();
                const displayNombre = nombreComp || data.nombre || data.razonSocial || 'SIN NOMBRE';

                // Normalización de CUIT / DNI
                const displayDoc = data.clienteCuit || data.clienteDni || data.cuit || data.dni || '-';

                // Normalización de Productos / Ramos a mostrar
                let productosDisplay = '-';
                if (Array.isArray(data.productoAprobar) && data.productoAprobar.length > 0) {
                    productosDisplay = data.productoAprobar.join(', ');
                } else if (data.riesgo) {
                    productosDisplay = data.subriesgo ? `${data.riesgo} (${data.subriesgo})` : data.riesgo;
                }

                return {
                    id: doc.id,
                    ...data,
                    displayNombre,
                    displayDoc,
                    productosDisplay,
                    estadoGestion: data.estadoGestion || 'Pendiente',
                    responsableAsignado: data.responsableAsignado || '',
                    fechaCarga: data.fechaCarga || '',
                    fechaProximoContacto: data.fechaProximoContacto || null,
                    canal: data.canal || '-',
                    canalNombre: data.canalNombre || '-',
                    subcanalNombre: data.subcanalNombre || '-'
                };
            });

            setItems(list);
            setLoading(false);
        }, (error) => {
            console.error("Error al sincronizar clientes_croselling:", error);
            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    // 2. HANDLER CENTRALIZADO DE ACCIONES (CREACIÓN, EDICIÓN, DETALLE, ELIMINACIÓN)
    const handleAction = useCallback((item, type) => {
        if (type === 'CREATE_CLEAR') {
            setEditingRecord(null);
        } else if (type === 'EDIT') {
            setEditingRecord(item);
        } else if (type === 'VIEW') {
            setDetailRecord(item);
            setView('detail');
        } else if (type === 'DELETE') {
            setAuthModalConfig({
                open: true,
                type: 'DELETE',
                item
            });
        }
    }, []);

    // 3. CONFIRMACIÓN DE ELIMINACIÓN
    const confirmDelete = useCallback(async () => {
        if (!authModalConfig.item?.id) return;
        try {
            await deleteClienteCrosellingFull(authModalConfig.item.id);
            setAuthModalConfig({ open: false, type: '', item: null });
        } catch (error) {
            console.error("Error al eliminar oportunidad de cross-selling:", error);
        }
    }, [authModalConfig.item]);

    const handleSuccess = useCallback(() => {
        setEditingRecord(null);
    }, []);

    // 4. LÓGICA DE FILTRADO Y ORDENAMIENTO
    const handleFilterChange = useCallback((newFilters) => {
        setFilterValues(newFilters);
        setCurrentPage(1);
    }, []);

    const filteredData = useMemo(() => {
        return rawItems.filter(item => {
            // Buscador General
            if (filterValues.search) {
                const searchLower = filterValues.search.toLowerCase();
                const matchNombre = item.displayNombre.toLowerCase().includes(searchLower);
                const matchDoc = item.displayDoc.toLowerCase().includes(searchLower);
                const matchProds = item.productosDisplay.toLowerCase().includes(searchLower);
                const matchResp = item.responsableAsignado.toLowerCase().includes(searchLower);
                if (!matchNombre && !matchDoc && !matchProds && !matchResp) return false;
            }

            // Filtros Selects Múltiples
            if (filterValues.estadoGestion?.length > 0) {
                if (!filterValues.estadoGestion.includes(item.estadoGestion)) return false;
            }
            if (filterValues.responsableAsignado?.length > 0) {
                if (!filterValues.responsableAsignado.includes(item.responsableAsignado)) return false;
            }
            if (filterValues.canal?.length > 0) {
                if (!filterValues.canal.includes(item.canal)) return false;
            }
            if (filterValues.canalNombre?.length > 0) {
                if (!filterValues.canalNombre.includes(item.canalNombre)) return false;
            }
            if (filterValues.subcanalNombre?.length > 0) {
                if (!filterValues.subcanalNombre.includes(item.subcanalNombre)) return false;
            }

            return true;
        });
    }, [rawItems, filterValues]);

    // 🎯 ORDENAMIENTO AVANZADO: MANTENER FECHAS NULAS/VACÍAS AL FINAL
    const sortedData = useMemo(() => {
        if (!sortConfig.key) return filteredData;

        return [...filteredData].sort((a, b) => {
            let aVal = a[sortConfig.key];
            let bVal = b[sortConfig.key];

            // SI SE ORDENA POR FECHA DE PRÓXIMO CONTACTO (AGENDA)
            if (sortConfig.key === 'fechaProximoContacto') {
                const hasA = Boolean(aVal);
                const hasB = Boolean(bVal);

                // Si uno tiene fecha y el otro no, el que NO tiene fecha siempre va al final
                if (hasA && !hasB) return -1;
                if (!hasA && hasB) return 1;
                if (!hasA && !hasB) return 0;

                // Comparación de fechas en formato ISO o YYYY-MM-DD
                const dateA = new Date(aVal).getTime();
                const dateB = new Date(bVal).getTime();

                if (dateA < dateB) return sortConfig.direction === 'asc' ? -1 : 1;
                if (dateA > dateB) return sortConfig.direction === 'asc' ? 1 : -1;
                return 0;
            }

            // ORDENAMIENTO ESTÁNDAR PARA OTRAS COLUMNAS
            if (!aVal && bVal) return 1;
            if (aVal && !bVal) return -1;
            if (!aVal && !bVal) return 0;

            if (typeof aVal === 'string') aVal = aVal.toLowerCase();
            if (typeof bVal === 'string') bVal = bVal.toLowerCase();

            if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
            if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        });
    }, [filteredData, sortConfig]);

    // PAGINACIÓN
    const totalPages = Math.ceil(sortedData.length / itemsPerPage) || 1;
    const paginatedData = useMemo(() => {
        const start = (currentPage - 1) * itemsPerPage;
        return sortedData.slice(start, start + itemsPerPage);
    }, [sortedData, currentPage, itemsPerPage]);

    const handleSort = useCallback((key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    }, []);

    const toggleColumn = useCallback((key) => {
        setColumnVisibility(prev => ({ ...prev, [key]: !prev[key] }));
    }, []);

    return {
        view, setView,
        loading,
        editingRecord, setEditingRecord,
        detailRecord, setDetailRecord,
        authModalConfig, setAuthModalConfig,
        itemsTable: rawItems,
        handleAction,
        confirmDelete,
        handleSuccess,
        tableState: {
            sortConfig,
            columnVisibility,
            currentPage, setCurrentPage,
            selectedRowId, setSelectedRowId,
            filterValues,
            filteredData: sortedData,
            paginatedData,
            totalPages,
            handleSort,
            toggleColumn,
            handleFilterChange
        }
    };
};